import { InjectionToken } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * Injection token used to pass data into a dynamically-opened dialog content component.
 *
 * Usage inside a content component:
 * ```typescript
 * constructor(@Inject(DIALOG_DATA) public data: MyData) {}
 * ```
 */
export const DIALOG_DATA = new InjectionToken<unknown>('DIALOG_DATA');

/**
 * Handle returned by `EnsembleDialogService.open()`.
 * Call `.close(result?)` to dismiss the dialog and emit the result.
 * Subscribe to `.afterClosed$` to react when the dialog is dismissed.
 */
export class DialogRef<R = unknown> {
  private readonly _afterClosed = new Subject<R | undefined>();

  /** Observable that emits once when the dialog is closed, then completes. */
  readonly afterClosed$: Observable<R | undefined> = this._afterClosed.asObservable();

  /** Material-compatible API for consumers expecting `dialogRef.afterClosed()`. */
  afterClosed(): Observable<R | undefined> {
    return this.afterClosed$;
  }

  /** Close the dialog, optionally passing a result value back to the opener. */
  close(result?: R): void {
    if (this._afterClosed.closed) return;
    this._afterClosed.next(result);
    this._afterClosed.complete();
  }
}

